import { inject, Injectable, InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';

import { MockCannesWifiConnectionService } from '../../mocks/hunting-device/mock-cannes-wifi-connection.service';
import { MockHuntingDeviceGate } from '../../mocks/hunting-device/mock-hunting-device-gate.service';

import type { CannesWifiConnectionService } from './cannes-wifi-connection.service';

// Separate token for the real service so the facade can hold a real instance while the app-wide
// CannesWifiConnectionService token resolves to the facade itself (see app.config.ts).
export const REAL_CANNES_WIFI_CONNECTION = new InjectionToken<CannesWifiConnectionService>('REAL_CANNES_WIFI_CONNECTION');

/**
 * Routes the AP provisioning flow to the real service or the mock based on MockHuntingDeviceGate,
 * so the dev toggle swaps them at runtime instead of build time.
 */
@Injectable()
export class CannesWifiConnectionFacade {
  // services
  private readonly real = inject(REAL_CANNES_WIFI_CONNECTION);
  private readonly mock = inject(MockCannesWifiConnectionService);
  private readonly gate = inject(MockHuntingDeviceGate);

  public provisionAndConnect$(deviceId: string): Observable<string> {
    return this.gate.isActive ? this.mock.provisionAndConnect$(deviceId) : this.real.provisionAndConnect$(deviceId);
  }
}

// Compile-time guard: fails the build if the facade drifts from the real service's public API.
type _PublicApi = Pick<CannesWifiConnectionService, 'provisionAndConnect$'>;
const _assert: _PublicApi = undefined as unknown as CannesWifiConnectionFacade;
void _assert;

// Fails the build when the real service gains a public member the facade does not forward —
// a missing forward otherwise surfaces only as a runtime TypeError (the app-wide token
// resolves to the facade).
type _MissingFromFacade = Exclude<keyof CannesWifiConnectionService, keyof CannesWifiConnectionFacade>;
const _assertNoneMissing: Record<_MissingFromFacade, never> = {};
void _assertNoneMissing;
